import { useEffect } from 'react';

const SEO = ({ 
  title, 
  description, 
  keywords, 
  image, 
  url, 
  type = 'website',
  author = 'Kashyap Engineering',
  publishedTime,
  modifiedTime
}) => {
  const siteName = 'Kashyap Engineering';
  const fullTitle = title ? `${title} | ${siteName}` : siteName;
  const defaultDescription = 'Leading fire safety solutions provider in India. Professional fire safety equipment, systems, and services meeting Indian Standards.';
  const defaultKeywords = 'fire safety, fire extinguishers, fire alarms, sprinkler systems, fire safety equipment, fire safety services, India, Kashyap Engineering';
  const defaultImage = '/logo.jpeg';
  const siteUrl = 'https://kashyapengineering.co.in'; // Update this with your actual domain

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Function to update or create meta tag
    const updateMetaTag = (name, content, property = false) => {
      let meta = document.querySelector(`meta[${property ? 'property' : 'name'}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Function to update or create link tag
    const updateLinkTag = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // Basic Meta Tags
    updateMetaTag('description', description || defaultDescription);
    updateMetaTag('keywords', keywords || defaultKeywords);
    updateMetaTag('author', author);
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('language', 'English');
    updateMetaTag('revisit-after', '7 days');

    // Open Graph Meta Tags
    updateMetaTag('og:title', fullTitle, true);
    updateMetaTag('og:description', description || defaultDescription, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', url || siteUrl, true);
    updateMetaTag('og:image', image || defaultImage, true);
    updateMetaTag('og:site_name', siteName, true);
    updateMetaTag('og:locale', 'en_IN', true);

    // Twitter Card Meta Tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', description || defaultDescription);
    updateMetaTag('twitter:image', image || defaultImage);
    updateMetaTag('twitter:site', '@kashyapengineering');

    // Update favicon and apple-touch-icon
    updateLinkTag('icon', defaultImage);
    updateLinkTag('apple-touch-icon', defaultImage);

    // Add structured data
    const addStructuredData = () => {
      // Remove existing structured data
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
      existingScripts.forEach(script => script.remove());

      // Organization structured data
      const organizationData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Kashyap Engineering",
        "url": siteUrl,
        "logo": `${siteUrl}/logo.jpeg`,
        "description": defaultDescription,
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "IN"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service"
        }
      };

      const organizationScript = document.createElement('script');
      organizationScript.type = 'application/ld+json';
      organizationScript.textContent = JSON.stringify(organizationData);
      document.head.appendChild(organizationScript);

      // WebSite structured data
      const websiteData = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": siteName,
        "url": siteUrl,
        "description": defaultDescription,
        "publisher": {
          "@type": "Organization",
          "name": siteName
        }
      };

      const websiteScript = document.createElement('script');
      websiteScript.type = 'application/ld+json';
      websiteScript.textContent = JSON.stringify(websiteData);
      document.head.appendChild(websiteScript);

      // Article structured data if provided
      if (publishedTime) {
        const articleData = {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": fullTitle,
          "description": description || defaultDescription,
          "image": image || defaultImage,
          "author": {
            "@type": "Organization",
            "name": author
          },
          "publisher": {
            "@type": "Organization",
            "name": siteName,
            "logo": {
              "@type": "ImageObject",
              "url": `${siteUrl}/logo.jpeg`
            }
          },
          "datePublished": publishedTime,
          "dateModified": modifiedTime || publishedTime,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": url || siteUrl
          }
        };

        const articleScript = document.createElement('script');
        articleScript.type = 'application/ld+json';
        articleScript.textContent = JSON.stringify(articleData);
        document.head.appendChild(articleScript);
      }
    };

    addStructuredData();

    // Cleanup function
    return () => {
      // Reset to default title when component unmounts
      document.title = siteName;
    };
  }, [title, description, keywords, image, url, type, author, publishedTime, modifiedTime, fullTitle, defaultDescription, defaultKeywords, defaultImage, siteUrl, siteName]);

  // This component doesn't render anything
  return null;
};

export default SEO;
